import Swal from "sweetalert2";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import i18next from "i18next";

import { ErrorAlert } from "@shared/ui/alerts";
import { FormatNumberMoney } from "@shared/utils/moneyFormat";

import {
  confirmCurrentMonthInvoice,
  confirmPreInvoice,
  sendInvoiceRequest,
} from "../api/preInvoiceApi";
import { HTTP_STATUS, PREINVOICE_ENDPOINT, SWAL_COLOR } from "../constants";

// Flujos imperativos (Swal) de generación y envío de factura. Son parte del
// public API del módulo: los consumen también admin (generación) e invoice
// (envío) con sus propias URLs. El HTTP va por la capa api/.

import type { TFunction } from "i18next";
import type { PreInvoice, PreInvoiceDetail } from "../api/preInvoiceApi";

/**
 * POLITICA DE CAMPOS NULABLES EN ESTE ARCHIVO
 *
 * En el backend `total`, `benefitDiscount`, `priceUnit`, `units`, `hours`, `month`, `year`
 * e `id` son todos boxed (`Double`, `Integer`, `Long`), asi que el contrato acierta al
 * declararlos opcionales: pueden venir null de verdad.
 *
 * Aca NO se los coacciona a 0. Este es el resumen que el colaborador confirma antes de
 * generar una factura: un importe faltante mostrado como "$0.00" es una mentira silenciosa
 * sobre una linea facturable, mientras que el "NaN" que aparece hoy al menos se ve. Se
 * conserva el comportamiento actual con casts y queda anotado para decidir en un ticket
 * si corresponde bloquear la confirmacion cuando falta un importe.
 *
 * (En `commission` se hizo lo contrario — ahi el total es informativo y un NaN no arriesga
 * nada. La diferencia es deliberada.)
 */

/** Callbacks de carga que las pages inyectan en todo este flujo. */
type LoadingToggle = () => void;

interface SendInvoiceOptions {
  period?: string;
  total?: string;
  onInvoiceSent?: () => void;
}

const formatPeriod = (month: number, year: number) =>
  format(new Date(year, month - 1, 1), "MMMM - yyyy", {
    locale: i18next.language === "es" ? es : enUS,
  }).toUpperCase();

/**
 * Pide email de copia y envía la factura. El email es opcional: la factura se
 * envía al confirmar, con o sin ccEmail. Condicionarlo a `if (email)` dejaría la
 * factura creada sin enviar (huérfana) y bloquearía el período.
 */
export const sendInvoice = async (
  thisLoading: LoadingToggle,
  notLoading: LoadingToggle,
  invoiceId: number,
  urlSendInvoice: string = PREINVOICE_ENDPOINT.SEND,
  t: TFunction,
  options: SendInvoiceOptions = {}
) => {
  const { period, total, onInvoiceSent } = options;

  const buildContextHtml = () => {
    if (!period) return "";
    const totalLine = total
      ? `<div>${t("totalInvoice")}: <strong>${total}</strong></div>`
      : "";
    return `<div>${t("period")}: <strong>${period}</strong></div>${totalLine}`;
  };

  const { value: email, isConfirmed } = await Swal.fire({
    icon: "success",
    title: t("invoiceGeneratedSuccessfully"),
    html: buildContextHtml(),
    input: "email",
    inputLabel: t("copyEmail"),
    inputPlaceholder: t("enterCopyEmail"),
    showCancelButton: true,
    confirmButtonColor: SWAL_COLOR.CONFIRM,
    cancelButtonColor: SWAL_COLOR.CANCEL,
    confirmButtonText: t("sentInvoice"),
    cancelButtonText: t("cancel"),
  });

  if (isConfirmed) {
    thisLoading();
    const request = { ccEmail: email || null, invoiceId };
    sendInvoiceRequest(urlSendInvoice, request)
      .then(() => {
        notLoading();
        Swal.fire({
          icon: "success",
          title: t("uploadInvoice"),
          text: t("carriedOutSuccessfully"),
          html: buildContextHtml() || undefined,
        }).then(() => {
          if (typeof onInvoiceSent === "function") {
            onInvoiceSent();
          }
        });
      })
      .catch((error) => {
        const data = error.response?.data;
        notLoading();
        ErrorAlert(t("errorUploadInvoice"), data?.message ?? t("internalError")).then();
      });
  }
};

const showConfirmationDialog = (
  thisLoading: LoadingToggle,
  notLoading: LoadingToggle,
  onActiveAddItemInvoiceDialog: () => void,
  preInvoiceId: number,
  month: number,
  year: number,
  invoiceTotal: number,
  currency: string,
  urlConfirmPreInvoice: string,
  urlSendInvoice: string | undefined,
  t: TFunction,
  onInvoiceGenerated?: () => void
) => {
  const periodLabel = formatPeriod(month, year);
  const totalLabel = FormatNumberMoney(currency).format(invoiceTotal);
  Swal.fire({
    title: t("generateInvoiceQuestion"),
    html: `<div>${t("period")}: <strong>${periodLabel}</strong></div>
           <div>${t("totalInvoice")}: <strong>${totalLabel}</strong></div>
           <br>${t("afterSendingNotModified")}`,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: SWAL_COLOR.CONFIRM,
    cancelButtonColor: SWAL_COLOR.CANCEL,
    confirmButtonText: t("confirm"),
    cancelButtonText: "No",
  }).then(({ isConfirmed }) => {
    if (isConfirmed) {
      thisLoading();
      confirmPreInvoice(urlConfirmPreInvoice, preInvoiceId)
        .then((data) => {
          notLoading();
          if (typeof onInvoiceGenerated === "function") {
            onInvoiceGenerated();
          }
          sendInvoice(thisLoading, notLoading, data.id as number, urlSendInvoice, t, {
            period: periodLabel,
            total: totalLabel,
            onInvoiceSent: onInvoiceGenerated,
          }).then();
        })
        .catch((error) => {
          notLoading();
          const errorData = error.response?.data;
          if (errorData && errorData.code === HTTP_STATUS.BAD_REQUEST) {
            ErrorAlert(t("errorToGenerateInvoice"), errorData.message).then();
            return;
          }
          ErrorAlert("Error", t("errorToGenerateInvoice")).then();
        });
    }
  });
};

const buildSummaryHtml = (
  details: PreInvoiceDetail[],
  currency: string,
  benefitDiscount: number,
  total: number,
  periodLine: string
) =>
  `<br> <h2>${i18next.t("resume")}</h2>
    <div>${periodLine}</div>
             ${details.map(
               (detail) =>
                 `<br>${detail.description}
             <div>${i18next.t("totalHours")}: ${detail.hours}</div>
             <div>${i18next.t("units")}: ${detail.units}</div>
             <div>${i18next.t("amount")}: ${FormatNumberMoney(currency).format(detail.total as number)}</div>
             `
             )}
            <br>${i18next.t("discountByBenefits")}: ${FormatNumberMoney(currency).format(-benefitDiscount)}
            <br>${i18next.t("total")}:  ${FormatNumberMoney(currency).format(total)}`;

/** Muestra el resumen de la prefactura del período por defecto y, si se confirma, genera la factura. */
export const generateInvoice = (
  data: PreInvoice,
  thisLoading: LoadingToggle,
  notLoading: LoadingToggle,
  onActiveAddItemInvoiceDialog: () => void,
  urlPreInvoicePending: string,
  urlConfirmPreInvoice: string,
  urlSendInvoice: string | undefined,
  onInvoiceGenerated?: () => void
) => {
  const { id, details, currency, total, benefitDiscount, month, year } = data;
  notLoading();

  Swal.fire({
    html: buildSummaryHtml(
      details as PreInvoiceDetail[],
      currency as string,
      benefitDiscount as number,
      total as number,
      `${i18next.t("period")}: ${formatPeriod(month as number, year as number)} `
    ),
    title: i18next.t("preInvoiceSummaryTitle"),
    icon: "info",
    showCancelButton: true,
    confirmButtonColor: SWAL_COLOR.CONFIRM,
    showDenyButton: true,
    cancelButtonColor: SWAL_COLOR.CANCEL,
    denyButtonColor: SWAL_COLOR.DENY,
    confirmButtonText: i18next.t("generateInvoice"),
    denyButtonText: i18next.t("addAdditionalConcept"),
    cancelButtonText: i18next.t("cancel"),
  }).then(({ isConfirmed, isDenied }) => {
    if (isConfirmed) {
      showConfirmationDialog(
        thisLoading,
        notLoading,
        onActiveAddItemInvoiceDialog,
        id as number,
        month as number,
        year as number,
        total as number,
        currency as string,
        urlConfirmPreInvoice,
        urlSendInvoice,
        i18next.t,
        onInvoiceGenerated
      );
    }
    if (isDenied) {
      onActiveAddItemInvoiceDialog();
    }
  });
};

// Convierte un concepto adicional del formulario (AddDetail) en una línea de detalle
// para mostrar en el resumen del preview.
const additionalItemToDetail = (item: PreInvoiceDetail) => ({
  description: item.description,
  hours: 0,
  units: item.units,
  total: Number(item.total) || 0,
});

// Paso final del flujo del mes actual (Camino A): confirma la generación y, tras confirmar,
// dispara la creación atómica de la factura (POST) y el envío. Solo acá se persiste.
const runCurrentMonthConfirmation = (
  additionalItems: PreInvoiceDetail[],
  month: number,
  year: number,
  total: number,
  currency: string,
  thisLoading: LoadingToggle,
  notLoading: LoadingToggle,
  onInvoiceGenerated?: () => void
) => {
  const periodLabel = formatPeriod(month, year);
  const totalLabel = FormatNumberMoney(currency).format(total);
  Swal.fire({
    title: i18next.t("generateInvoiceQuestion"),
    html: `<div>${i18next.t("period")}: <strong>${periodLabel}</strong></div>
           <div>${i18next.t("totalInvoice")}: <strong>${totalLabel}</strong></div>
           <br>${i18next.t("afterSendingNotModified")}`,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: SWAL_COLOR.CONFIRM,
    cancelButtonColor: SWAL_COLOR.CANCEL,
    confirmButtonText: i18next.t("confirm"),
    cancelButtonText: "No",
  }).then(({ isConfirmed }) => {
    if (!isConfirmed) return;
    thisLoading();
    confirmCurrentMonthInvoice(additionalItems)
      .then((invoice) => {
        notLoading();
        if (typeof onInvoiceGenerated === "function") {
          onInvoiceGenerated();
        }
        sendInvoice(thisLoading, notLoading, invoice.id as number, PREINVOICE_ENDPOINT.SEND, i18next.t, {
          period: periodLabel,
          total: totalLabel,
          onInvoiceSent: onInvoiceGenerated,
        }).then();
      })
      .catch((error) => {
        notLoading();
        const errorData = error.response?.data;
        if (errorData && errorData.code === HTTP_STATUS.BAD_REQUEST) {
          ErrorAlert(i18next.t("errorToGenerateInvoice"), errorData.message).then();
          return;
        }
        ErrorAlert("Error", i18next.t("errorToGenerateInvoice")).then();
      });
  });
};

/**
 * Flujo de generación de la prefactura del MES ACTUAL (Camino A). Muestra el resumen a partir
 * del preview (que NO persistió nada) más los conceptos adicionales que el usuario haya cargado,
 * y solo al confirmar dispara la creación atómica de la factura. Cancelar no deja nada creado.
 * Es un flujo dedicado del colaborador: no reemplaza a generateInvoice (que usa admin).
 */
export const confirmCurrentMonthPreInvoice = (
  preview: PreInvoice,
  additionalItems: PreInvoiceDetail[],
  thisLoading: LoadingToggle,
  notLoading: LoadingToggle,
  options: { onAddConcept?: () => void; onInvoiceGenerated?: () => void } = {}
) => {
  const { onAddConcept, onInvoiceGenerated } = options;
  const { currency, benefitDiscount, month, year } = preview;

  const extraLines = (additionalItems ?? []).map(additionalItemToDetail);
  const details = [...(preview.details ?? []), ...extraLines];
  const extraTotal = extraLines.reduce((acc, line) => acc + line.total, 0);
  const total = (preview.total ?? 0) + extraTotal;

  Swal.fire({
    html: buildSummaryHtml(
      details as PreInvoiceDetail[],
      currency as string,
      benefitDiscount as number,
      total as number,
      `${i18next.t("period")}: ${formatPeriod(month as number, year as number)} `
    ),
    title: i18next.t("preInvoiceSummaryTitle"),
    icon: "info",
    showCancelButton: true,
    confirmButtonColor: SWAL_COLOR.CONFIRM,
    showDenyButton: true,
    cancelButtonColor: SWAL_COLOR.CANCEL,
    denyButtonColor: SWAL_COLOR.DENY,
    confirmButtonText: i18next.t("generateInvoice"),
    denyButtonText: i18next.t("addAdditionalConcept"),
    cancelButtonText: i18next.t("cancel"),
  }).then(({ isConfirmed, isDenied }) => {
    if (isConfirmed) {
      runCurrentMonthConfirmation(
        additionalItems,
        month as number,
        year as number,
        total as number,
        currency as string,
        thisLoading,
        notLoading,
        onInvoiceGenerated
      );
    }
    if (isDenied && typeof onAddConcept === "function") {
      onAddConcept();
    }
    // Cancelar: no se hace nada. Como el resumen viene del preview, no quedó nada persistido.
  });
};

/** Igual que generateInvoice pero para un período (mes/año) elegido por el usuario. */
export const generateInvoiceByPeriod = (
  data: PreInvoice,
  thisLoading: LoadingToggle,
  notLoading: LoadingToggle,
  onActiveAddItemInvoiceDialog: () => void,
  urlPreInvoicePending: string,
  urlConfirmPreInvoice: string,
  urlSendInvoice: string,
  month: number,
  year: number,
  onInvoiceGenerated?: () => void
) => {
  const { id, details, currency, total, benefitDiscount } = data;
  notLoading();

  Swal.fire({
    html: buildSummaryHtml(
      details as PreInvoiceDetail[],
      currency as string,
      benefitDiscount as number,
      total as number,
      `${i18next.t("selectedPeriod")}: ${formatPeriod(month as number, year as number)} `
    ),
    title: i18next.t("preInvoiceSummaryForPeriod", {
      month: format(new Date(year, month - 1, 1), "MMMM", {
        locale: i18next.language === "es" ? es : enUS,
      }),
      year,
    }),
    icon: "info",
    showCancelButton: true,
    confirmButtonColor: SWAL_COLOR.CONFIRM,
    showDenyButton: true,
    cancelButtonColor: SWAL_COLOR.CANCEL,
    denyButtonColor: SWAL_COLOR.DENY,
    confirmButtonText: i18next.t("generateInvoice"),
    denyButtonText: i18next.t("addAdditionalConcept"),
    cancelButtonText: i18next.t("cancel"),
  }).then(({ isConfirmed, isDenied }) => {
    if (isConfirmed) {
      showConfirmationDialog(
        thisLoading,
        notLoading,
        onActiveAddItemInvoiceDialog,
        id as number,
        month as number,
        year as number,
        total as number,
        currency as string,
        urlConfirmPreInvoice,
        urlSendInvoice,
        i18next.t,
        onInvoiceGenerated
      );
    }
    if (isDenied) {
      onActiveAddItemInvoiceDialog();
    }
  });
};
